import { format } from 'date-fns';
import type { SongRequest } from '../services/requests';
import type { PrizeType } from '../services/prizes';
import { attendeeDisplayName } from './attendeeDisplayName';

export type PredefinedAwardId =
  | 'top-contributor' | 'hidden-gem' | 'best-taste' | 'crowd-mover' | 'kingmaker'
  | 'first-on-the-floor' | 'night-owl' | 'dj-whisperer'
  | 'crowd-favorite' | 'diamond-in-the-rough';

export interface AwardDefinition {
  id: PredefinedAwardId;
  name: string;
  emoji: string;
  tagline: string;
  howWon: string;
  rankedOnly: boolean;
}

export interface AwardPick {
  categoryId: string;
  name: string;
  emoji: string;
  tagline: string;
  custom: boolean;
  winnerId: string | null;
  winnerName: string | null;
  stat: string | null;
  overridden: boolean;
  prizeType: PrizeType | null;
  prizeCustomText: string | null;
}

export interface AwardReveal {
  categoryId: string;
  name: string;
  emoji: string;
  tagline: string;
  winnerId: string;
  winnerName: string;
  stat: string | null;
  howWon: string | null;
  revealedAt: string;
}

export type EventAwardsStatus = 'announced' | 'revealing' | 'done';

export interface EventAwards {
  eventId: string;
  djId: string;
  status: EventAwardsStatus;
  upcoming: Array<{ name: string; emoji: string }>;
  totalCount: number;
  revealed: AwardReveal[];
  startedAt: string | null;
  updatedAt: string;
}

export interface AwardDraft {
  eventId: string;
  djId: string;
  picks: AwardPick[];
  showNamesEarly: boolean;
  computedAt: string | null;
  updatedAt: string;
}

export interface AwardVote {
  requestId: string;
  oderId: string;
  type: 'up' | 'down' | undefined;
  votedAtMs: number | null;
}

export interface AwardScores {
  consensus: Record<string, number>;
  discovery: Record<string, number>;
}

export interface CustomAwardInput {
  categoryId: string;
  name: string;
  emoji: string;
  tagline: string;
}

export interface AwardInput {
  selectedIds: PredefinedAwardId[];
  customAwards: CustomAwardInput[];
  requests: SongRequest[];
  votes: AwardVote[];
  rankingAttendeeIds: string[];
  scores: AwardScores | null;
  isRanked: boolean;
  djId: string;
  existing: AwardPick[];
}

export const DEFAULT_CUSTOM_AWARD_EMOJI = '🏅';
export const CUSTOM_AWARD_NAME_MAX = 40;
export const CUSTOM_AWARD_TAGLINE_MAX = 80;

export const PICKED_BY_DJ = 'Picked by the DJ';

export const AWARD_DEFINITIONS: AwardDefinition[] = [
  {
    id: 'top-contributor', name: 'Top Contributor', emoji: '🎤', tagline: 'Most likely to run the aux',
    howWon: 'Had the most of their requested songs played tonight', rankedOnly: false,
  },
  {
    id: 'hidden-gem', name: 'Hidden Gem', emoji: '💎', tagline: 'Found the song everyone loved',
    howWon: 'Requested the single most-upvoted song of the night', rankedOnly: false,
  },
  {
    id: 'best-taste', name: 'Best Taste', emoji: '👌', tagline: 'Never missed with a request',
    howWon: 'Highest average upvotes per request, with at least 2 requests', rankedOnly: false,
  },
  {
    id: 'crowd-mover', name: 'Crowd Mover', emoji: '🙌', tagline: 'Got the whole room voting',
    howWon: 'Most upvotes across all of their requests', rankedOnly: false,
  },
  {
    id: 'kingmaker', name: 'Kingmaker', emoji: '👑', tagline: 'Backed the songs that made the night',
    howWon: 'Upvoted the most songs that went on to play', rankedOnly: false,
  },
  {
    id: 'first-on-the-floor', name: 'First on the Floor', emoji: '🕺', tagline: 'First one in, no hesitation',
    howWon: 'First to request or vote tonight', rankedOnly: false,
  },
  {
    id: 'night-owl', name: 'Night Owl', emoji: '🦉', tagline: 'Last one still requesting',
    howWon: 'Last to request or vote tonight', rankedOnly: false,
  },
  {
    id: 'dj-whisperer', name: 'DJ Whisperer', emoji: '🎧', tagline: 'Always knew what the DJ would play',
    howWon: 'Highest share of requests the DJ approved, with at least 2 decided', rankedOnly: false,
  },
  {
    id: 'crowd-favorite', name: 'Crowd Favorite', emoji: '⭐', tagline: 'The crowd ranked their songs on top',
    howWon: "Their songs ranked highest in the crowd's rankings", rankedOnly: true,
  },
  {
    id: 'diamond-in-the-rough', name: 'Diamond in the Rough', emoji: '🌱', tagline: 'Saw a hit before anyone else did',
    howWon: 'Requested a song that ranked far higher with the people who found it than with the crowd overall', rankedOnly: true,
  },
];

const APPROVED_STATUSES: ReadonlySet<SongRequest['status']> = new Set(['queued', 'nowPlaying', 'played']);

interface AttendeeStats {
  oderId: string;
  nickname: string | null;
  nicknameAtMs: number;
  requests: SongRequest[];
  playedCount: number;
  upvoteTotal: number;
  approvedCount: number;
  decidedCount: number;
  upvotesOnPlayed: number;
  firstActivityMs: number | null;
  lastActivityMs: number | null;
}

interface Candidate {
  oderId: string;
  value: number;
  stat: string;
}

function plural(count: number, singular: string, pluralForm: string): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

function requestedAtMs(request: SongRequest): number | null {
  const ms = Date.parse(request.timestamps?.requested ?? '');
  return Number.isNaN(ms) ? null : ms;
}

function isUpvote(vote: AwardVote): boolean {
  return vote.type !== 'down';
}

function emptyStats(oderId: string): AttendeeStats {
  return {
    oderId,
    nickname: null,
    nicknameAtMs: -Infinity,
    requests: [],
    playedCount: 0,
    upvoteTotal: 0,
    approvedCount: 0,
    decidedCount: 0,
    upvotesOnPlayed: 0,
    firstActivityMs: null,
    lastActivityMs: null,
  };
}

function recordActivity(stats: AttendeeStats, ms: number | null): void {
  if (ms === null) return;
  if (stats.firstActivityMs === null || ms < stats.firstActivityMs) stats.firstActivityMs = ms;
  if (stats.lastActivityMs === null || ms > stats.lastActivityMs) stats.lastActivityMs = ms;
}

function eligibleRequests(requests: SongRequest[], djId: string): SongRequest[] {
  return requests.filter((request) =>
    !request.cancelledByAttendee
    && !request.practiceMode
    && Boolean(request.requestedBy?.oderId)
    && request.requestedBy.oderId !== djId);
}

function buildStats(input: AwardInput): Map<string, AttendeeStats> {
  const statsById = new Map<string, AttendeeStats>();
  const statsFor = (oderId: string) => {
    let stats = statsById.get(oderId);
    if (!stats) {
      stats = emptyStats(oderId);
      statsById.set(oderId, stats);
    }
    return stats;
  };

  for (const request of eligibleRequests(input.requests, input.djId)) {
    const stats = statsFor(request.requestedBy.oderId);
    const atMs = requestedAtMs(request);
    stats.requests.push(request);
    stats.upvoteTotal += request.upvoteCount || 0;
    if (request.status === 'played') stats.playedCount += 1;
    if (APPROVED_STATUSES.has(request.status)) stats.approvedCount += 1;
    if (APPROVED_STATUSES.has(request.status) || request.status === 'rejected') stats.decidedCount += 1;
    if (request.requestedBy.nickname && (atMs ?? -Infinity) >= stats.nicknameAtMs) {
      stats.nickname = request.requestedBy.nickname;
      stats.nicknameAtMs = atMs ?? -Infinity;
    }
    recordActivity(stats, atMs);
  }

  const playedIds = new Set(input.requests
    .filter((request) => request.status === 'played' && !request.cancelledByAttendee && !request.practiceMode)
    .map((request) => request.id));
  for (const vote of input.votes) {
    if (!vote.oderId || vote.oderId === input.djId) continue;
    const stats = statsFor(vote.oderId);
    if (isUpvote(vote) && playedIds.has(vote.requestId)) stats.upvotesOnPlayed += 1;
    recordActivity(stats, vote.votedAtMs);
  }

  return statsById;
}

function firstActivityOrLast(stats: AttendeeStats | undefined): number {
  return stats?.firstActivityMs ?? Infinity;
}

function pickBest(candidates: Candidate[], statsById: Map<string, AttendeeStats>): Candidate | null {
  if (candidates.length === 0) return null;
  const sorted = [...candidates].sort((a, b) =>
    b.value - a.value
    || firstActivityOrLast(statsById.get(a.oderId)) - firstActivityOrLast(statsById.get(b.oderId))
    || (a.oderId < b.oderId ? -1 : a.oderId > b.oderId ? 1 : 0));
  return sorted[0];
}

function pickByTime(
  statsById: Map<string, AttendeeStats>,
  timeOf: (stats: AttendeeStats) => number | null,
  earliest: boolean,
): AttendeeStats | null {
  const withTime = [...statsById.values()].filter((stats) => timeOf(stats) !== null);
  if (withTime.length === 0) return null;
  withTime.sort((a, b) => {
    const diff = earliest ? timeOf(a)! - timeOf(b)! : timeOf(b)! - timeOf(a)!;
    return diff || (a.oderId < b.oderId ? -1 : a.oderId > b.oderId ? 1 : 0);
  });
  return withTime[0];
}

function formatClock(ms: number): string {
  return format(ms, 'h:mm a');
}

function computeCategory(
  id: PredefinedAwardId,
  statsById: Map<string, AttendeeStats>,
  input: AwardInput,
): Candidate | null {
  const everyone = [...statsById.values()];
  switch (id) {
    case 'top-contributor':
      return pickBest(everyone
        .filter((stats) => stats.playedCount >= 1)
        .map((stats) => ({ oderId: stats.oderId, value: stats.playedCount, stat: plural(stats.playedCount, 'song played', 'songs played') })), statsById);
    case 'hidden-gem': {
      const perRequest = everyone.flatMap((stats) => stats.requests
        .filter((request) => (request.upvoteCount || 0) >= 1)
        .map((request) => ({
          oderId: stats.oderId,
          value: request.upvoteCount,
          stat: `“${request.song.title}” got ${plural(request.upvoteCount, 'upvote', 'upvotes')}`,
          atMs: requestedAtMs(request) ?? Infinity,
        })));
      perRequest.sort((a, b) => b.value - a.value || a.atMs - b.atMs);
      const best = pickBest(perRequest, statsById);
      if (!best) return null;
      return perRequest.find((entry) => entry.oderId === best.oderId && entry.value === best.value) ?? best;
    }
    case 'best-taste':
      return pickBest(everyone
        .filter((stats) => stats.requests.length >= 2 && stats.upvoteTotal > 0)
        .map((stats) => {
          const average = stats.upvoteTotal / stats.requests.length;
          return { oderId: stats.oderId, value: average, stat: `${average.toFixed(1)} upvotes per request` };
        }), statsById);
    case 'crowd-mover':
      return pickBest(everyone
        .filter((stats) => stats.upvoteTotal >= 1)
        .map((stats) => ({ oderId: stats.oderId, value: stats.upvoteTotal, stat: `${plural(stats.upvoteTotal, 'upvote', 'upvotes')} in total` })), statsById);
    case 'kingmaker':
      return pickBest(everyone
        .filter((stats) => stats.upvotesOnPlayed >= 1)
        .map((stats) => ({
          oderId: stats.oderId,
          value: stats.upvotesOnPlayed,
          stat: `Upvoted ${plural(stats.upvotesOnPlayed, 'song', 'songs')} that played`,
        })), statsById);
    case 'first-on-the-floor': {
      const first = pickByTime(statsById, (stats) => stats.firstActivityMs, true);
      return first ? { oderId: first.oderId, value: 0, stat: `First in at ${formatClock(first.firstActivityMs!)}` } : null;
    }
    case 'night-owl': {
      const last = pickByTime(statsById, (stats) => stats.lastActivityMs, false);
      return last ? { oderId: last.oderId, value: 0, stat: `Still going at ${formatClock(last.lastActivityMs!)}` } : null;
    }
    case 'dj-whisperer':
      return pickBest(everyone
        .filter((stats) => stats.decidedCount >= 2)
        .map((stats) => {
          const percent = stats.approvedCount * 100 / stats.decidedCount;
          return { oderId: stats.oderId, value: percent, stat: `${Math.round(percent)}% of requests approved` };
        }), statsById);
    case 'crowd-favorite': {
      if (!input.isRanked || !input.scores) return null;
      const consensus = input.scores.consensus;
      return pickBest(everyone.flatMap((stats) => {
        const scored = stats.requests.filter((request) => typeof consensus[request.id] === 'number');
        if (scored.length === 0) return [];
        const average = scored.reduce((sum, request) => sum + consensus[request.id], 0) / scored.length;
        return [{ oderId: stats.oderId, value: average, stat: 'Top crowd ranking' }];
      }), statsById);
    }
    case 'diamond-in-the-rough': {
      if (!input.isRanked || !input.scores) return null;
      const { consensus, discovery } = input.scores;
      return pickBest(everyone.flatMap((stats) => stats.requests
        .filter((request) => typeof consensus[request.id] === 'number' && typeof discovery[request.id] === 'number')
        .map((request) => ({ oderId: stats.oderId, value: discovery[request.id] - consensus[request.id], stat: 'A crowd discovery' }))
        .filter((candidate) => candidate.value > 0)), statsById);
    }
  }
}

function nameFor(oderId: string, statsById: Map<string, AttendeeStats>): string {
  return attendeeDisplayName(statsById.get(oderId)?.nickname, oderId);
}

export function computeAwards(input: AwardInput): AwardPick[] {
  const statsById = buildStats(input);
  const existingById = new Map(input.existing.map((pick) => [pick.categoryId, pick]));
  const selected = new Set(input.selectedIds);

  const predefined = AWARD_DEFINITIONS
    .filter((definition) => selected.has(definition.id))
    .map((definition): AwardPick => {
      const existing = existingById.get(definition.id);
      const prize = { prizeType: existing?.prizeType ?? null, prizeCustomText: existing?.prizeCustomText ?? null };
      const base = { categoryId: definition.id, name: definition.name, emoji: definition.emoji, tagline: definition.tagline, custom: false };
      if (existing?.overridden) {
        return { ...base, ...prize, winnerId: existing.winnerId, winnerName: existing.winnerName, stat: null, overridden: true };
      }
      const winner = computeCategory(definition.id, statsById, input);
      return {
        ...base,
        ...prize,
        winnerId: winner?.oderId ?? null,
        winnerName: winner ? nameFor(winner.oderId, statsById) : null,
        stat: winner?.stat ?? null,
        overridden: false,
      };
    });

  const custom = input.customAwards.map((award): AwardPick => {
    const existing = existingById.get(award.categoryId);
    return {
      categoryId: award.categoryId,
      name: award.name,
      emoji: award.emoji,
      tagline: award.tagline,
      custom: true,
      winnerId: existing?.winnerId ?? null,
      winnerName: existing?.winnerName ?? null,
      stat: null,
      overridden: existing?.overridden ?? false,
      prizeType: existing?.prizeType ?? null,
      prizeCustomText: existing?.prizeCustomText ?? null,
    };
  });

  return [...predefined, ...custom];
}

export function buildAttendeeOptions(
  requests: SongRequest[],
  votes: AwardVote[],
  rankingAttendeeIds: string[],
  djId: string,
): Array<{ id: string; name: string }> {
  const nicknames = new Map<string, { nickname: string | null; atMs: number }>();
  const remember = (oderId: string, nickname: string | null, atMs: number) => {
    const current = nicknames.get(oderId);
    if (!current) {
      nicknames.set(oderId, { nickname, atMs });
    } else if (nickname && atMs >= current.atMs) {
      nicknames.set(oderId, { nickname, atMs });
    }
  };

  for (const request of eligibleRequests(requests, djId)) {
    remember(request.requestedBy.oderId, request.requestedBy.nickname || null, requestedAtMs(request) ?? -Infinity);
  }
  for (const vote of votes) {
    if (vote.oderId && vote.oderId !== djId) remember(vote.oderId, null, -Infinity);
  }
  for (const attendeeId of rankingAttendeeIds) {
    if (attendeeId && attendeeId !== djId) remember(attendeeId, null, -Infinity);
  }

  return [...nicknames.entries()]
    .map(([id, entry]) => ({ id, name: attendeeDisplayName(entry.nickname, id) }))
    .sort((a, b) => a.name.localeCompare(b.name) || (a.id < b.id ? -1 : 1));
}

export function blankPick(definition: AwardDefinition): AwardPick {
  return {
    categoryId: definition.id,
    name: definition.name,
    emoji: definition.emoji,
    tagline: definition.tagline,
    custom: false,
    winnerId: null,
    winnerName: null,
    stat: null,
    overridden: false,
    prizeType: null,
    prizeCustomText: null,
  };
}

export function nextCustomCategoryId(picks: AwardPick[]): string {
  const numbers = picks
    .filter((pick) => pick.custom)
    .map((pick) => Number(pick.categoryId.replace('custom-', '')))
    .filter((value) => Number.isFinite(value));
  return `custom-${numbers.length === 0 ? 1 : Math.max(...numbers) + 1}`;
}

export function isPredefinedAwardId(categoryId: string): categoryId is PredefinedAwardId {
  return AWARD_DEFINITIONS.some((definition) => definition.id === categoryId);
}

export function revealablePicks(picks: AwardPick[]): AwardPick[] {
  return picks.filter((pick) => pick.winnerId !== null);
}

export function buildUpcoming(picks: AwardPick[]): Array<{ name: string; emoji: string }> {
  return revealablePicks(picks).map((pick) => ({ name: pick.name, emoji: pick.emoji }));
}

export function toReveal(pick: AwardPick, nowIso: string): AwardReveal {
  if (!pick.winnerId) {
    throw new Error(`Award ${pick.categoryId} has no winner to reveal`);
  }
  return {
    categoryId: pick.categoryId,
    name: pick.name,
    emoji: pick.emoji,
    tagline: pick.tagline,
    winnerId: pick.winnerId,
    winnerName: pick.winnerName ?? attendeeDisplayName(null, pick.winnerId),
    stat: pick.stat,
    howWon: pick.custom || pick.overridden
      ? PICKED_BY_DJ
      : AWARD_DEFINITIONS.find((definition) => definition.id === pick.categoryId)?.howWon ?? null,
    revealedAt: nowIso,
  };
}
